import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {fetchPcategories} from "../../../../../../reduxLib/pcategoriesLib";
import {fetchColorsByTextiles} from "../../../../../../reduxLib/colorsLib";
import {uploadProductImages} from "../../../../../../Utilities/uploadHelperProd";
import {createFile, deleteFile} from "../../../../../../reduxLib/filesPhLib";

/**
 * EDIT PRODUCT MODAL with deferred image handling
 *
 * Image Workflow:
 * - All image operations deferred until form submission (save)
 * - Existing images: can be deleted (marked), labeled, reordered
 * - New images: uploaded on submit, never created if deleted before save
 * - Main image: first non-deleted item in filesRelated array
 * - Deleted images: soft-deleted until save (can be restored)
 */
export default function EditProductModal({ open, setOpen, product, onSubmit }) {
    const sizeMap = {
        "5": "Small",
        "4": "Medium",
        "3": "Large",
    };

    const buildDimensions = (w, h, d) => {
        const W = (w ?? "").toString().trim();
        const H = (h ?? "").toString().trim();
        const D = (d ?? "").toString().trim();
        if (!W || !H || !D) return null;
        return `${W}x${H}x${D} εκ.`;
    };

    const toNumberOrNull = (v) => {
        if (v === null || v === undefined) return null;
        const s = String(v).trim();
        if (s === "") return null;
        const n = Number(s);
        return Number.isFinite(n) ? n : null;
    };

    const dispatch = useDispatch();
    const catstate = useSelector((state) => state.categoryreducer.state);
    const categories = useSelector((state) => state.categoryreducer.pcategoriesList);
    const textilesStatus = useSelector((state) => state.colorreducer.textilesStatus);
    const textilesColor = useSelector((state) => state.colorreducer.textilesColor);

    const FILES_BASE_URL = "";

    const toFileUrl = (fileLocation) => {
        if (!fileLocation) return "";
        if (/^https?:\/\//i.test(fileLocation)) return fileLocation;
        return `${FILES_BASE_URL}${fileLocation}`;
    };

    // STATE
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [filesRelated, setFilesRelated] = useState([]); // Image list with preview
    const [editingImageLabel, setEditingImageLabel] = useState(null);
    const [colorsToRender, setColorsToRender] = useState([]);
    const [textilesValue, setTextilesValue] = useState("");
    const [plexis, setPlexis] = useState("");
    const [closingTextiles, setClosingTextiles] = useState("");
    const [ConstructionEffortDays, setConstructionEffortDays] = useState("");
    const [productSize, setProductSize] = useState("");
    const [size, setSize] = useState("");

    const fileInputRef = useRef(null);

    // Initialize from product
    useEffect(() => {
        if (!product) return;

        const initialImages = (product.filesRelated ?? []).map((f) => ({
            id: f.file_ID,
            label: f.label ?? f.name ?? "Image",
            url: toFileUrl(f.fileLocation),
            _file: null,
            _isNew: false,
            _deleted: false,
            // Backend fields
            fileType: f.fileType,
            fileLocation: f.fileLocation,
            dateCreated: f.dateCreated,
        }));

        setFilesRelated(initialImages);
        setTextilesValue(product?.textiles ?? "");
        setPlexis(product?.plexis ?? "");
        setClosingTextiles(product?.closingTextiles ?? "");
        setConstructionEffortDays(product?.constructionEffortDays ?? "");
        setProductSize(product?.productSize ?? "");
        setSize(product?.size ?? "");
        setColorsToRender(product?.colors ?? []);
        setEditingImageLabel(null);
    }, [product, open]);

    // Fetch categories
    useEffect(() => {
        if (!categories || categories.length === 0) {
            dispatch(fetchPcategories()).then((action) => {
                if (action.payload) {
                    setCategoryOptions(action.payload);
                }
            });
        } else {
            setCategoryOptions(categories);
        }
    }, [dispatch, catstate]);

    // Size derivation
    useEffect(() => {
        if (!productSize) {
            setSize("");
            return;
        }
        setSize(sizeMap[String(productSize)] ?? "");
    }, [productSize]);

    // Textiles -> Colors
    useEffect(() => {
        if (!textilesValue) {
            setColorsToRender([]);
            return;
        }
        if (textilesValue === (product?.textiles ?? "")) {
            setColorsToRender(product?.colors ?? []);
            return;
        }
        setColorsToRender([]);
        dispatch(fetchColorsByTextiles(textilesValue));
    }, [textilesValue, product, dispatch]);

    useEffect(() => {
        if (!textilesValue) return;
        if (textilesValue === (product?.textiles ?? "")) return;
        if (textilesStatus === "success" || textilesStatus === "fulfilled") {
            setColorsToRender(textilesColor ?? []);
        }
        if (textilesStatus === "error" || textilesStatus === "rejected") {
            setColorsToRender([]);
        }
    }, [textilesValue, product, textilesStatus, textilesColor]);

    if (!open) return null;

    // ==================== IMAGE HANDLERS ====================

    const handleAddImages = (e) => {
        const newFiles = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!newFiles.length) return;

        const newItems = newFiles.map((file, idx) => ({
            id: `new-${Date.now()}-${idx}`,
            label: file.name,
            url: URL.createObjectURL(file),
            _file: file,
            _isNew: true,
            _deleted: false,
        }));

        setFilesRelated((prev) => [...newItems, ...prev]);
    };

    const handleDeleteImage = (id) => {
        setFilesRelated((prev) =>
            prev.map((img) =>
                img.id === id ? { ...img, _deleted: true } : img
            )
        );
    };

    const handleRestoreImage = (id) => {
        setFilesRelated((prev) =>
            prev.map((img) =>
                img.id === id ? { ...img, _deleted: false } : img
            )
        );
    };

    const handleSetMainImage = (id) => {
        setFilesRelated((prev) => {
            const image = prev.find((img) => img.id === id);
            if (!image) return prev;
            return [image, ...prev.filter((img) => img.id !== id)];
        });
    };

    const handleMoveImageUp = (id) => {
        setFilesRelated((prev) => {
            const idx = prev.findIndex((img) => img.id === id);
            if (idx <= 0) return prev;
            const newArr = [...prev];
            [newArr[idx], newArr[idx - 1]] = [newArr[idx - 1], newArr[idx]];
            return newArr;
        });
    };

    const handleMoveImageDown = (id) => {
        setFilesRelated((prev) => {
            const idx = prev.findIndex((img) => img.id === id);
            if (idx >= prev.length - 1) return prev;
            const newArr = [...prev];
            [newArr[idx], newArr[idx + 1]] = [newArr[idx + 1], newArr[idx]];
            return newArr;
        });
    };

    const handleUpdateImageLabel = (id, newLabel) => {
        setFilesRelated((prev) =>
            prev.map((img) =>
                img.id === id ? { ...img, label: newLabel } : img
            )
        );
        setEditingImageLabel(null);
    };

    // ==================== FORM PROCESSING ====================

    const formDataToProductObject = (fd) => {
        // Colors
        const colorPool = [
            ...(Array.isArray(product?.colors) ? product.colors : []),
            ...(Array.isArray(colorsToRender) ? colorsToRender : []),
        ];
        const colorById = new Map(
            colorPool.map((c) => [Number(c.pcid ?? c.id), c]).filter(([id]) => Number.isFinite(id))
        );
        const selectedIds = (fd.getAll("selectedColorIds") ?? [])
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n));
        const selectedColors = selectedIds
            .map((id) => colorById.get(id))
            .filter(Boolean);

        // Dimensions
        const width = toNumberOrNull(fd.get("width"));
        const height = toNumberOrNull(fd.get("height"));
        const depth = toNumberOrNull(fd.get("depth"));
        const dimentions = buildDimensions(width, height, depth) ?? (product?.dimentions ?? null);

        // Separate images
        const activeImages = filesRelated.filter((img) => !img._deleted);
        const existingImages = activeImages.filter((img) => !img._isNew);
        const newImages = activeImages.filter((img) => img._isNew);

        const obj = {
            productID: product?.productID,
            name: (fd.get("name") ?? "").toString(),
            categoryRelId: toNumberOrNull(fd.get("category")) ?? product?.categoryRelId,
            details: (fd.get("details") ?? "").toString(),
            description: (fd.get("description") ?? "").toString(),
            price: toNumberOrNull(fd.get("price")) ?? 0,
            constructionEffortDays: toNumberOrNull(fd.get("constructionEffortDays")) ?? product?.constructionEffortDays ?? null,
            rating: product?.rating ?? null,
            estimationDeliveryDays: product?.estimationDeliveryDays ?? null,
            textiles: (fd.get("textiles") ?? "").toString() || null,
            plexis: (fd.get("plexis") ?? "").toString() || null,
            closingTextiles: (fd.get("closingTextiles") ?? "").toString() || null,
            productSize: (fd.get("productSize") ?? "").toString(),
            size: (fd.get("size") ?? "").toString() || null,
            colors: selectedColors,
            dimentions,
            isAvailable: product?.isAvailable ?? true,
            canBeOrdered: product?.canBeOrdered ?? true,
            isPopular: product?.isPopular ?? false,
            dateAdded: product?.dateAdded ?? null,
            // Existing images with updated labels
            filesRelated: existingImages.map((img) => ({
                file_ID: img.id,
                fileType: img.fileType ?? "PRODUCT_IMAGE",
                productRelId: img.productRelId ?? product?.productID,
                name: img.label,
                fileLocation: img.fileLocation,
                dateCreated: img.dateCreated,
            })),
        };

        return {
            obj,
            newImages,
            deletedImageIds: filesRelated
                .filter((img) => img._deleted && !img._isNew)
                .map((img) => img.id),
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const formData = new FormData(e.target);
            const { obj, newImages, deletedImageIds } = formDataToProductObject(formData);
            const activeImagesOrdered = filesRelated.filter((img) => !img._deleted);

            // 1) Upload new images
            const newImageUrls = [];
            if (newImages.length > 0) {
                const uploadedUrls = await uploadProductImages(
                    product?.name || "product",
                    newImages.map((img) => img._file)
                );
                newImageUrls.push(...(uploadedUrls || []));
            }

            // 2) Create file metadata for new images
            const createdFilesByTempId = new Map();
            for (let i = 0; i < newImageUrls.length; i++) {
                const url = newImageUrls[i];
                const newImg = newImages[i];
                if (!url || !newImg) continue;

                const filesCreator = {
                    Name: newImg.label,
                    FileLocation: url,
                    DateCreated: new Date(),
                    fileType: "PRODUCT_IMAGE",
                    productRelId: Number(product?.productID),
                };

                const createAction = await dispatch(createFile(filesCreator));
                const createdPayload = createAction?.payload ?? createAction;

                createdFilesByTempId.set(newImg.id, {
                    file_ID:
                        createdPayload?.file_ID ??
                        createdPayload?.fileId ??
                        createdPayload?.id ??
                        null,
                    fileType: createdPayload?.fileType ?? "PRODUCT_IMAGE",
                    productRelId:
                        createdPayload?.productRelId ?? Number(product?.productID),
                    name:
                        createdPayload?.name ?? createdPayload?.Name ?? newImg.label,
                    fileLocation:
                        createdPayload?.fileLocation ??
                        createdPayload?.FileLocation ??
                        url,
                    dateCreated:
                        createdPayload?.dateCreated ??
                        createdPayload?.DateCreated ??
                        new Date().toISOString(),
                });
            }

            // Merge existing + newly created files in final active order
            const existingById = new Map(
                (obj.filesRelated ?? []).map((f) => [String(f.file_ID), f])
            );

            obj.filesRelated = activeImagesOrdered
                .map((img) => {
                    if (img._isNew) {
                        return createdFilesByTempId.get(img.id) ?? null;
                    }
                    return (
                        existingById.get(String(img.id)) ?? {
                            file_ID: img.id,
                            fileType: img.fileType ?? "PRODUCT_IMAGE",
                            productRelId: img.productRelId ?? Number(product?.productID),
                            name: img.label,
                            fileLocation: img.fileLocation,
                            dateCreated: img.dateCreated,
                        }
                    );
                })
                .filter(Boolean);

            // 3) Delete removed images
            for (const fileId of deletedImageIds) {
                try {
                    await dispatch(deleteFile(fileId));
                } catch (err) {
                    console.error(`Failed to delete image ${fileId}:`, err);
                }
            }

            // 4) Call parent handler with updated product
            onSubmit?.(obj);
            setOpen(false);
        } catch (err) {
            console.error("Error during product update:", err);
            alert(`Error saving product: ${err?.message || "Unknown error"}`);
        }
    };

    // ==================== RENDER ====================

    return (
        <div
            className="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setOpen(false)}
        >
            <div
                className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-serif font-bold text-custom-green">
                        Edit Product
                    </h2>
                    <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                        <i className="fa-solid fa-times text-xl" />
                    </button>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                    {/* Name + Category */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                            <input
                                type="text"
                                name="name"
                                required
                                defaultValue={product?.name ?? ""}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                            <select
                                name="category"
                                required
                                defaultValue={product?.categoryRelId ?? ""}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            >
                                <option value="">Select Category</option>
                                {categories.length > 0 ? (
                                    categories.map((c) => (
                                        <option key={c.categoryID ?? c.pid} value={c.categoryID ?? c.pid}>
                                            {c.name}
                                        </option>
                                    ))
                                ) : (
                                    <option value="-1">not found</option>
                                )}
                            </select>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                        <textarea
                            name="description"
                            rows={3}
                            required
                            defaultValue={product?.description ?? ""}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                        />
                    </div>

                    {/* Details */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Details *</label>
                        <textarea
                            name="details"
                            rows={4}
                            required
                            defaultValue={product?.details ?? ""}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                        />
                    </div>

                    {/* Price */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Price (€) *</label>
                            <input
                                type="number"
                                name="price"
                                step="0.01"
                                required
                                defaultValue={product?.price ?? ""}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Size + Dimensions */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Product Size (Enum)
                            </label>
                            <select
                                value={productSize}
                                onChange={(e) => setProductSize(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            >
                                <option value="">Select</option>
                                <option value="5">Small</option>
                                <option value="4">Medium</option>
                                <option value="3">Large</option>
                            </select>
                            <input type="hidden" name="productSize" value={productSize} />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Size (Derived)
                            </label>
                            <input
                                type="text"
                                value={size}
                                readOnly
                                className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
                            />
                            <input type="hidden" name="size" value={size} />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Dimensions (cm)
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                <input
                                    type="number"
                                    name="width"
                                    placeholder="Width"
                                    defaultValue={product?.width ?? ""}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green"
                                />
                                <input
                                    type="number"
                                    name="height"
                                    placeholder="Height"
                                    defaultValue={product?.height ?? ""}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green"
                                />
                                <input
                                    type="number"
                                    name="depth"
                                    placeholder="Depth"
                                    defaultValue={product?.depth ?? ""}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Construction Days
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={ConstructionEffortDays}
                                onChange={(e) => setConstructionEffortDays(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                            />
                            <input
                                type="hidden"
                                name="constructionEffortDays"
                                value={ConstructionEffortDays}
                            />
                        </div>
                    </div>

                    {/* Special Properties */}
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Special Properties
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Textiles
                                </label>
                                <select
                                    value={textilesValue}
                                    onChange={(e) => setTextilesValue(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                >
                                    <option value="">None</option>
                                    <option value="bambakero">bambakero</option>
                                    <option value="bambakeroLourex">bambakeroLourex</option>
                                    <option value="forte">forte</option>
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    Με βάση αυτό εμφανίζονται τα διαθέσιμα colors.
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plexis
                                </label>
                                <select
                                    value={plexis}
                                    onChange={(e) => setPlexis(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                >
                                    <option value="">None</option>
                                    <option value="basic">basic</option>
                                    <option value="kagelaki">kagelaki</option>
                                    <option value="grammh">grammh</option>
                                    <option value="vafla">vafla</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Closing Textiles
                                </label>
                                <select
                                    value={closingTextiles}
                                    onChange={(e) => setClosingTextiles(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-custom-green focus:border-transparent"
                                >
                                    <option value="">Δεν έχει</option>
                                    <option value="SIMPLE">Απλό</option>
                                    <option value="CABOURAS">Καβούρας</option>
                                    <option value="AXTIDA">Αχτίδα</option>
                                    <option value="TSOUREKI">Τσουρέκι</option>
                                </select>
                            </div>
                        </div>

                        <input type="hidden" name="textiles" value={textilesValue} />
                        <input type="hidden" name="plexis" value={plexis} />
                        <input type="hidden" name="closingTextiles" value={closingTextiles} />
                    </div>

                    {/* Colors */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Available Colors
                        </label>

                        <div className="grid grid-cols-4 md:grid-cols-6 gap-3 max-h-40 overflow-y-auto p-2 border rounded-lg">
                            {(colorsToRender ?? []).map((c) => {
                                const id = Number(c.pcid ?? c.id);
                                const isOriginalProductTextiles =
                                    textilesValue === (product?.textiles ?? "");
                                return (
                                    <label key={id} className="flex flex-col items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="selectedColorIds"
                                            value={id}
                                            defaultChecked={isOriginalProductTextiles}
                                            className="mb-1"
                                        />
                                        <div
                                            className="w-8 h-8 rounded-full border-2"
                                            style={{ backgroundColor: c.hexColor ?? c.hex ?? "#ffffff" }}
                                            title={c.colorName ?? c.ColorName ?? ""}
                                        />
                                        <span className="text-[11px] mt-1 text-center leading-tight">
                                            {c.colorName ?? c.ColorName ?? "Color"}
                                        </span>
                                    </label>
                                );
                            })}

                            {(colorsToRender ?? []).length === 0 && (
                                <div className="col-span-full text-sm text-gray-500 p-2">
                                    No colors found for this product.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Images */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                                Product Images
                            </label>
                            <span className="text-xs text-gray-500">
                                {filesRelated.filter((img) => !img._deleted).length} images
                            </span>
                        </div>

                        {/* Hidden file input */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleAddImages}
                        />

                        {/* Image grid */}
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                            {filesRelated.map((img, idx) => {
                                const isDeleted = img._deleted;
                                const isNew = img._isNew;
                                const isMain = idx === 0 && !isDeleted;
                                const isEditing = editingImageLabel === img.id;

                                return (
                                    <div
                                        key={img.id}
                                        className={`border rounded-lg overflow-hidden transition-opacity ${
                                            isDeleted ? "opacity-40" : "opacity-100"
                                        }`}
                                    >
                                        <div className="aspect-square bg-gray-50 relative">
                                            <img
                                                src={img.url}
                                                alt={img.label}
                                                className="w-full h-full object-cover"
                                                draggable={false}
                                            />

                                            {isMain && (
                                                <div className="absolute top-1 right-1 bg-custom-green text-white text-[10px] px-2 py-0.5 rounded font-semibold">
                                                    MAIN
                                                </div>
                                            )}

                                            {isNew && (
                                                <div className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded">
                                                    NEW
                                                </div>
                                            )}

                                            {isDeleted && (
                                                <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                                                    <span className="text-xs font-semibold text-red-700">DELETED</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Label editor */}
                                        <div className="p-2 bg-white">
                                            {isEditing ? (
                                                <div className="flex gap-1 mb-2">
                                                    <input
                                                        type="text"
                                                        value={img.label}
                                                        onChange={(e) =>
                                                            setFilesRelated((prev) =>
                                                                prev.map((im) =>
                                                                    im.id === img.id
                                                                        ? { ...im, label: e.target.value }
                                                                        : im
                                                                )
                                                            )
                                                        }
                                                        autoFocus
                                                        className="flex-1 text-xs px-2 py-1 border rounded"
                                                        placeholder="Image label"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleUpdateImageLabel(img.id, img.label)
                                                        }
                                                        className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                                    >
                                                        ✓
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="text-[11px] text-gray-600 truncate mb-2" title={img.label}>
                                                    {img.label}
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <div className="flex flex-col gap-1">
                                                {!isDeleted ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingImageLabel(img.id)}
                                                            className="w-full px-1 py-0.5 border rounded hover:bg-blue-50 text-xs"
                                                        >
                                                            Edit Label
                                                        </button>

                                                        {!isMain && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSetMainImage(img.id)}
                                                                className="w-full px-1 py-0.5 border rounded hover:bg-green-50 text-xs"
                                                            >
                                                                Set Main
                                                            </button>
                                                        )}

                                                        <div className="flex gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMoveImageUp(img.id)}
                                                                disabled={idx === 0}
                                                                className="flex-1 px-1 py-0.5 border rounded hover:bg-gray-100 disabled:opacity-30 text-xs"
                                                            >
                                                                ↑
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMoveImageDown(img.id)}
                                                                disabled={idx === filesRelated.length - 1}
                                                                className="flex-1 px-1 py-0.5 border rounded hover:bg-gray-100 disabled:opacity-30 text-xs"
                                                            >
                                                                ↓
                                                            </button>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteImage(img.id)}
                                                            className="w-full px-1 py-0.5 border rounded hover:bg-red-50 text-red-600 text-xs"
                                                        >
                                                            Delete
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreImage(img.id)}
                                                        className="w-full px-1 py-0.5 border rounded hover:bg-yellow-50 text-yellow-600 text-xs"
                                                    >
                                                        Restore
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {filesRelated.filter((img) => !img._deleted).length === 0 && (
                                <div className="col-span-full text-sm text-gray-500 border rounded-lg p-4">
                                    No images yet.
                                </div>
                            )}
                        </div>

                        {/* Upload button */}
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-custom-green hover:bg-gray-50 transition"
                        >
                            <i className="fa-solid fa-cloud-upload-alt text-3xl text-gray-400 mb-2" />
                            <p className="text-gray-600">Click to add more images</p>
                            <p className="text-xs text-gray-500 mt-1">Images will be uploaded when you save</p>
                        </button>
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end space-x-4 pt-4">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="px-6 py-2 bg-custom-green text-white rounded-lg hover:bg-opacity-90"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
